// Booking Service for Hotel Reservations
export interface BookingRequest {
    hotelId: string;
    roomId: string;
    checkIn: string;
    checkOut: string;
    guests: number;
    guestInfo: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        specialRequests?: string;
    };
}

export interface BookingResponse {
    success: boolean;
    bookingId?: string;
    message: string;
    totalAmount?: number;
    confirmationNumber?: string;
}

export interface RoomAvailability {
    roomId: string;
    available: boolean;
    price: number;
    availableRooms: number;
}

export interface AvailabilityRequest {
    hotelId: string;
    checkIn: string;
    checkOut: string;
    guests: number;
}

class BookingService {
    private bookings: Map<string, BookingRequest & { bookingId: string; status: string; createdAt: string }> = new Map();

    async checkAvailability(request: AvailabilityRequest): Promise<RoomAvailability[]> {
        return new Promise((resolve) => {
            setTimeout(() => {
                // Mock availability data
                const mockRooms: RoomAvailability[] = [
                    {
                        roomId: '1',
                        available: true,
                        price: 250,
                        availableRooms: 3
                    },
                    {
                        roomId: '2',
                        available: true,
                        price: 450,
                        availableRooms: 2
                    },
                    {
                        roomId: '3',
                        available: false,
                        price: 850,
                        availableRooms: 0
                    }
                ];

                // Simulate some rooms being unavailable based on dates
                const checkInDate = new Date(request.checkIn);
                const dayOfWeek = checkInDate.getDay();
                
                // Make some rooms unavailable on weekends
                if (dayOfWeek === 0 || dayOfWeek === 6) {
                    mockRooms[0].availableRooms = Math.max(0, mockRooms[0].availableRooms - 1);
                    mockRooms[1].availableRooms = Math.max(0, mockRooms[1].availableRooms - 1);
                }

                resolve(mockRooms);
            }, 1000);
        });
    }

    async createBooking(request: BookingRequest): Promise<BookingResponse> {
        return new Promise((resolve) => {
            setTimeout(() => {
                // Validate request
                if (!request.hotelId || !request.roomId || !request.checkIn || !request.checkOut) {
                    resolve({
                        success: false,
                        message: 'Missing required booking information'
                    });
                    return;
                }

                if (!request.guestInfo.firstName || !request.guestInfo.lastName || !request.guestInfo.email) {
                    resolve({
                        success: false,
                        message: 'Missing guest information'
                    });
                    return;
                }

                // Generate booking ID
                const bookingId = 'BK' + Date.now().toString(36).toUpperCase();
                const confirmationNumber = Math.random().toString(36).substring(2, 8).toUpperCase();

                // Calculate total amount (mock calculation)
                const checkInDate = new Date(request.checkIn);
                const checkOutDate = new Date(request.checkOut);
                const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
                
                let basePrice = 250; // Default price
                if (request.roomId === '2') basePrice = 450;
                if (request.roomId === '3') basePrice = 850;

                const totalAmount = basePrice * nights;

                // Store booking
                this.bookings.set(bookingId, {
                    ...request,
                    bookingId,
                    status: 'confirmed',
                    createdAt: new Date().toISOString()
                });

                console.log(`[MOCK] Booking created: ${bookingId} for ${request.guestInfo.firstName} ${request.guestInfo.lastName}`);

                resolve({
                    success: true,
                    bookingId,
                    message: 'Booking confirmed successfully!',
                    totalAmount,
                    confirmationNumber
                });
            }, 1500);
        });
    }

    async getBooking(bookingId: string): Promise<BookingRequest & { bookingId: string; status: string; createdAt: string } | null> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const booking = this.bookings.get(bookingId);
                resolve(booking || null);
            }, 500);
        });
    }

    async cancelBooking(bookingId: string): Promise<BookingResponse> {
        return new Promise((resolve) => {
            setTimeout(() => {
                const booking = this.bookings.get(bookingId);
                if (!booking) {
                    resolve({
                        success: false,
                        message: 'Booking not found'
                    });
                    return;
                }

                booking.status = 'cancelled';
                this.bookings.set(bookingId, booking);

                resolve({
                    success: true,
                    message: 'Booking cancelled successfully'
                });
            }, 1000);
        });
    }
}

export const bookingService = new BookingService();

